import './_sidebar-cart.scss';
import 'snippets/icon/icon';
import debounce from 'lodash-es/debounce';
import { cartSubtotalUpdate } from 'snippets/cart-js/cart-helpers';

const LOADING_CLASS = 'loading';
const SIDEBAR_CART_PRICE_SELECTOR = '.sidebar-cart-item-price';
const SIDEBAR_CART_TOTAL_SELECTOR = '.sidebar-cart-total';
const SIDEBAR_CART_DISCOUNT_SELECTOR = '.sidebar-cart-discount';
const NO_SCROLL_CLASS = 'noscroll';
const SIDEBAR_CART_ACTIVE_CLASS = 'sidebar-cart--active';
const CART_LINK_SELECTOR = 'div.header__link--cart';
const SIDEBAR_CART_ITEM_SELECTOR = '.sidebar-cart-item-wrapper';
const SIDEBAR_CART_SELECTOR = 'sidebar-cart';
const SIDEBAR_CART_FOOTER_SELECTOR = '.sidebar-cart-footer';

class SidebarCart extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('open', () => {
            this.updateEntireSection();
            this.open();
        });
        this.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }

    connectedCallback() {
        this.setUpCloseSidebarCartEvents();
        this.updateFreeShippingProgress();
    }

    open() {
        this.classList.add(SIDEBAR_CART_ACTIVE_CLASS);
        document.body.classList.add(NO_SCROLL_CLASS);
        this.focus({ preventScroll: true });
        const upsellSection = this.querySelector('.product-upsells');
        if(upsellSection) {
            upsellSection.updatePaginationButtons();
        }
    }

    close() {
        this.classList.remove(SIDEBAR_CART_ACTIVE_CLASS);
        document.body.classList.remove(NO_SCROLL_CLASS);
    }

    async updateEntireSection() {
        const parsed = await this.getParsedSidebarCart();
        const newSection = parsed.querySelector(SIDEBAR_CART_SELECTOR);
        if(newSection) {
            this.innerHTML = newSection.innerHTML;
        }

        this.updateFreeShippingProgress();
        this.removePriceLoadingClass();
        this.setUpCloseSidebarCartEvents();
    }

    async updateIndividualSection(sectionSelector) {
        const parsed = await this.getParsedSidebarCart();
        const newSection = parsed.querySelector(sectionSelector);
        const currentSection = this.querySelector(sectionSelector);
        if(newSection && currentSection) {
            currentSection.innerHTML = newSection.innerHTML;
        }

        // Update footer
        const cartFooter = this.querySelector(SIDEBAR_CART_FOOTER_SELECTOR);
        const newCartFooter = parsed.querySelector(SIDEBAR_CART_FOOTER_SELECTOR);
        if (cartFooter && newCartFooter) {
            cartFooter.innerHTML = newCartFooter.innerHTML;
        }

        // Update cart total
        const cartSubtotal = this.querySelector(SIDEBAR_CART_TOTAL_SELECTOR);
        const newCartSubtotal = parsed.querySelector(SIDEBAR_CART_TOTAL_SELECTOR);
        if(cartSubtotal && newCartSubtotal) {
            cartSubtotal.innerHTML = newCartSubtotal.innerHTML;
        }

        // Update cart discount
        const cartDiscount = this.querySelector(SIDEBAR_CART_DISCOUNT_SELECTOR);
        const newCartDiscount = parsed.querySelector(SIDEBAR_CART_DISCOUNT_SELECTOR);
        if(cartDiscount && newCartDiscount) {
            cartDiscount.innerHTML = newCartDiscount.innerHTML;
        } else if (cartDiscount && !newCartDiscount) {
            cartDiscount.remove();
            const cartDiscountLabel = this.querySelector('.sidebar-cart-discount-label');
            if(cartDiscountLabel) {
                cartDiscountLabel.remove();
            }
        } else if (!cartDiscount && newCartDiscount) {
            const cartDiscountRow = this.querySelector('.sidebar-cart-summary-row--discount');
            const newCartDiscountRow = parsed.querySelector('.sidebar-cart-summary-row--discount');
            if(cartDiscountRow && newCartDiscountRow) {
                cartDiscountRow.innerHTML = newCartDiscountRow.innerHTML;
            }
        }

        this.updateFreeShippingProgress();
        this.removePriceLoadingClass();
        this.setUpCloseSidebarCartEvents();
    }

    async getParsedSidebarCart() {
        const res = await fetch(window.localeUrl + '/?section_id=sidebar-cart');
        const text = await res.text();
        const parser = new DOMParser();
        return parser.parseFromString(text, 'text/html');
    }

    removePriceLoadingClass() {
        this.querySelectorAll(SIDEBAR_CART_PRICE_SELECTOR).forEach((price) => {
            price.classList.remove(LOADING_CLASS);
        });
        const cartTotal = this.querySelector(SIDEBAR_CART_TOTAL_SELECTOR);
        if (cartTotal) {
            cartTotal.classList.remove(LOADING_CLASS);
        }
        const cartDiscount = this.querySelector(SIDEBAR_CART_DISCOUNT_SELECTOR);
        if (cartDiscount) {
            cartDiscount.classList.remove(LOADING_CLASS);
        }
    }

    setUpCloseSidebarCartEvents() {
        this.querySelectorAll('.sidebar-cart__overlay, .sidebar-cart__close-button svg').forEach(element => {
            element.addEventListener('click', () => {
                this.close();
            });
        });
    }

    updateFreeShippingProgress() {
        Shopify.freeShippingProgress().init();
    }
}

class SidebarCartItem extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('change', debounce((e) => this.onQuantityChange(e.detail), 500));
    }

    async onQuantityChange(quantity) {
        this.querySelector(SIDEBAR_CART_PRICE_SELECTOR).classList.add(LOADING_CLASS);
        const cartTotal = document.querySelector(SIDEBAR_CART_TOTAL_SELECTOR);
        if(cartTotal) {
            cartTotal.classList.add(LOADING_CLASS);
        }
        const cartDiscount = document.querySelector(SIDEBAR_CART_DISCOUNT_SELECTOR);
        if (cartDiscount) {
            cartDiscount.classList.add(LOADING_CLASS);
        }
        const key = this.getAttribute('data-line-item-key');
        await updateItemQuantity(key, quantity);
    }
}

class SidebarCartRemoveButton extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('click', this.onRemove);
    }

    async onRemove() {
        document.querySelector(SIDEBAR_CART_TOTAL_SELECTOR).classList.add(LOADING_CLASS);
        const cartDiscount = document.querySelector(SIDEBAR_CART_DISCOUNT_SELECTOR);
        if (cartDiscount) {
            cartDiscount.classList.add(LOADING_CLASS);
        }
        this.classList.add('disabled');
        const rootItem = this.closest(SIDEBAR_CART_ITEM_SELECTOR);
        const key = rootItem.getAttribute('data-line-item-key');
        await updateItemQuantity(key, 0);
    }
}

class QuantityButton extends HTMLElement {
    constructor() {
        super();
        this.addEventListener('click', this.onQuantityChange);
    }

    onQuantityChange() {
        const rootItem = this.closest(SIDEBAR_CART_ITEM_SELECTOR);
        const quantityInput = rootItem.querySelector('input');
        const currentQuantity = Number(quantityInput.value);
        const isUp = this.classList.contains(
            'sidebar-cart-quantity-increase'
        );
        const newQuantity = isUp ? currentQuantity + 1 : currentQuantity - 1;
        const decreasingFromAboveMaxQuantity = newQuantity > 5 && !isUp;
        if(decreasingFromAboveMaxQuantity || newQuantity <= 5) {
            quantityInput.value = `${newQuantity}`;
            rootItem.dispatchEvent(new CustomEvent('change', { detail: `${newQuantity}` }));
        }

        if((isUp && newQuantity === 5) || (!isUp && newQuantity === 1)) {
            this.classList.add('disabled');
        } else {
            this.classList.remove('disabled');
        }
    }
}

function updateCartItemCounts(count) {
    document.querySelectorAll('.cart__item-count').forEach((el) => {
        el.textContent = count;
    });
}

async function updateItemQuantity(itemKey, newQuantity) {
    await fetch(window.localeUrl + '/cart/change.js', {
        method: 'post',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity: newQuantity, id: itemKey }),
    });
    await updateCart();
}

export async function updateCart() {
    const res = await fetch(window.localeUrl + '/cart.js', {
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json'
        }
    });
    const cart = await res.json();
    updateCartItemCounts(cart.item_count);
    const sidebarCart = document.querySelector(SIDEBAR_CART_SELECTOR);
    if(sidebarCart) {
        cart.item_count > 0
            ? await sidebarCart.updateIndividualSection('.sidebar-cart-items')
            : await sidebarCart.updateEntireSection();
    }
    cartSubtotalUpdate(cart);
}

document.querySelectorAll(CART_LINK_SELECTOR).forEach((cartLink) => {
    cartLink.addEventListener('click', (e) => {
        e.preventDefault();
        const sidebarCart = document.querySelector(SIDEBAR_CART_SELECTOR);
        if(sidebarCart !== null) {
            sidebarCart.open();
        }
    });
    cartLink.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const sidebarCart = document.querySelector(SIDEBAR_CART_SELECTOR);
            if(sidebarCart !== null) {
                sidebarCart.open();
            }
        }
    });
});

document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
        const sidebarCart = document.querySelector(SIDEBAR_CART_SELECTOR);
        if(sidebarCart !== null) {
            sidebarCart.close();
        }
    }
});

window.addEventListener('resize', () => {
    const sidebarCart = document.querySelector(SIDEBAR_CART_SELECTOR);
    if(sidebarCart !== null) {
        sidebarCart.close();
        sidebarCart.classList.add('hidden');
        window.setTimeout(() => {
            sidebarCart.classList.remove('hidden');
        }, 500);

    }
});

customElements.define('sidebar-cart', SidebarCart);
customElements.define('sidebar-cart-remove-button', SidebarCartRemoveButton);
customElements.define('sidebar-cart-item', SidebarCartItem);
customElements.define('quantity-button', QuantityButton);
